import * as React from 'react';

import {
  createClient,
  isAuthenticationResponse,
  isInvalidCredentialsError,
} from '@rematter/frontend-lib-api-client';
import { useAtom } from 'jotai';
import { jwtDecode } from 'jwt-decode';
import { useLocation } from 'wouter';

import {
  $accessToken,
  $facilityId,
  $user,
  $impersonator,
  $organization,
} from './identity.localState';
import { IdentityContext } from './IdentityContext';
import { RefreshOverlay } from './RefreshOverlay';

import { config } from '~/config';
import { createSignInRedirectPath } from '~/shared/routing/authRedirect';

export const IdentityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  /*
    This specific client is to prevent a catch-22 where the API depends on the access token
    to access private graphql queries/mutations. This client will always be unauthenticated,
    used exclusively to refresh authentication token and to sign out. A possible change
    here would be to create a route in the gateway to handle refreshes, outside of graphql.
  */
  const api = React.useCallback(
    (operation: `${'query' | 'mutation'}.${string}.${string}`) =>
      createClient({
        url: `${config.api.url}/graphql?q=${operation}`,
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'include',
      }),
    [],
  );

  const [, setLocation] = useLocation();

  const [isLoading, setIsLoading] = React.useState(false);
  const [accessToken, setAccessToken] = useAtom($accessToken);
  const [user, setUser] = useAtom($user);
  const [organization, setOrganization] = useAtom($organization);
  const [impersonator, setImpersonator] = useAtom($impersonator);
  const [facilityId, setFacilityId] = useAtom($facilityId);

  // Refresh retry state
  const [isShowingRefreshOverlay, setIsShowingRefreshOverlay] =
    React.useState(false);

  // Add cleanup function reference
  const timeoutRef = React.useRef<NodeJS.Timeout>(undefined);
  const retryTimeoutRef = React.useRef<NodeJS.Timeout>(undefined);

  const refreshAccessToken = React.useCallback(
    async (attemptNumber = 0) => {
      setIsLoading(true);

      try {
        const { authenticationRefreshSession } = await api(
          'mutation.authentication.refreshSession',
        ).mutation({
          authenticationRefreshSession: {
            __typename: true,
            __args: {
              clientId: config.workOs.clientId ?? '',
            },
            on_AuthenticationResponse: {
              accessToken: true,
              user: {
                __scalar: true,
              },
              organization: {
                __scalar: true,
                entitlements: true,
              },
            },
            on_InvalidCredentialsError: {
              email: true,
            },
          },
        });

        if (isAuthenticationResponse(authenticationRefreshSession)) {
          setAccessToken(authenticationRefreshSession.accessToken);
          setUser(authenticationRefreshSession.user);
          setOrganization(authenticationRefreshSession.organization);
          setImpersonator(
            authenticationRefreshSession.impersonator
              ? {
                  email: authenticationRefreshSession.impersonator.email,
                  reason:
                    authenticationRefreshSession.impersonator.reason ??
                    undefined,
                }
              : null,
          );
          // Success - reset retry state
          setIsShowingRefreshOverlay(false);
          setIsLoading(false);
          return;
        }

        if (isInvalidCredentialsError(authenticationRefreshSession)) {
          throw new Error('Invalid credentials');
        }
      } catch (error) {
        console.error(
          `Error while refreshing session (attempt ${attemptNumber + 1}/5):`,
          error,
        );

        const nextAttempt = attemptNumber + 1;

        // Show overlay after first failure
        if (nextAttempt >= 1) {
          setIsShowingRefreshOverlay(true);
        }

        // Retry up to 5 times
        if (nextAttempt < 5) {
          // Wait 5 seconds before retrying
          retryTimeoutRef.current = setTimeout(() => {
            refreshAccessToken(nextAttempt);
          }, 5000);
        } else {
          // All retries exhausted - revert to login
          console.info('Refresh token expired. New session is required.');
          setAccessToken(null);
          setUser(null);
          setOrganization(null);
          setImpersonator(null);
          setIsShowingRefreshOverlay(false);
          setIsLoading(false);
          setLocation(createSignInRedirectPath());
        }
      }
    },
    [
      api,
      setAccessToken,
      setUser,
      setOrganization,
      setImpersonator,
      setIsLoading,
      setLocation,
    ],
  );

  const scheduleTokenRefresh = React.useCallback(
    (token: string) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const decodedToken = jwtDecode(token);
      const exp = decodedToken.exp;
      if (!exp) return;

      const expirationTime = exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const oneMinute = 60 * 1000;

      // Schedule refresh 1 minute before expiration
      const timeUntilRefresh = expirationTime - currentTime - oneMinute;

      if (timeUntilRefresh >= 0) {
        timeoutRef.current = setTimeout(
          refreshAccessToken,
          Math.max(timeUntilRefresh, 1),
        );
      } else {
        // If token is already expired, refresh right away
        setIsLoading(true);
        refreshAccessToken();
      }
    },
    [refreshAccessToken],
  );

  // Modify signOut to clear the timeout and cleanup TalkJS session
  const signOut = async () => {
    setIsLoading(true);
    try {
      await api('mutation.authentication.signOut').mutation({
        authenticationSignOut: true,
      });
    } catch (error) {
      console.error('Error while signing out:', error);
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      // Reset retry state
      setIsShowingRefreshOverlay(false);

      // Cleanup TalkJS session IMMEDIATELY to ensure user goes offline
      console.log(
        '🚪 IdentityProvider signOut: accessing global TalkJS session',
      );
      const windowRecord = window as unknown as Record<string, unknown>;
      const globalSession = windowRecord.__talkJSSession as
        | { destroy: () => void }
        | undefined;
      if (globalSession) {
        try {
          console.log(
            '🔥 IdentityProvider: Destroying global TalkJS session to ensure user goes offline immediately',
          );
          globalSession.destroy();
          console.log(
            '✅ IdentityProvider: Global TalkJS session destroyed successfully',
          );
          windowRecord.__talkJSSession = null;
        } catch (error) {
          console.warn(
            '⚠️ IdentityProvider: Error destroying global TalkJS session:',
            error,
          );
        }
      } else {
        console.log(
          '⚠️ IdentityProvider: No global TalkJS session available to destroy',
        );
      }

      // Also try the context-based cleanup (this might be a no-op if context is gone)
      console.log(
        '🧹 IdentityProvider signOut: calling cleanupAllSubscriptions as backup',
      );
      console.log(
        '✅ IdentityProvider signOut: cleanupAllSubscriptions completed',
      );

      setAccessToken(null);
      setUser(null);
      setOrganization(null);
      setImpersonator(null);
      setFacilityId(null);

      setIsLoading(false);
    }
  };

  // Set up initial refresh schedule when accessToken changes
  React.useEffect(() => {
    if (accessToken) {
      scheduleTokenRefresh(accessToken);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [accessToken, user, impersonator, scheduleTokenRefresh]);

  return (
    <IdentityContext.Provider
      value={{
        accessToken,
        user,
        organization,
        impersonator,
        facilityId,
        isLoading: isLoading && !accessToken,
        isLoggedIn: !!accessToken,
        setAccessToken,
        setUser,
        setOrganization,
        setFacilityId,
        signOut,
      }}
    >
      {children}
      <RefreshOverlay isVisible={isShowingRefreshOverlay} onLogout={signOut} />
    </IdentityContext.Provider>
  );
};
